import { sumarTarifas } from '@tanteo/compartido';
import { ErrorDeApi } from './error-api';
import { EMBEBIDO_IMPUESTOS, impuestosDe } from './api';
import { supabase } from './supabase';

/**
 * Las filas que van a las plantillas de importación de Siigo (docs/research/mercado-proveedores.md
 * §A3). Devuelven datos; quien los convierte en un archivo es `ui/base/descargar.ts`, porque
 * descargar es una acción del navegador y no acceso a datos.
 */

/**
 * La plantilla de Siigo espera un porcentaje por producto: la columna «Impuesto» lleva la suma de
 * las tarifas vigentes. El desglose por tributo no cabe en este formato.
 */
export async function datosExporteProductos(negocioId: string): Promise<(string | number | null)[][]> {
  const { data, error, status } = await supabase
    .from('productos')
    .select(`sku, nombre, categoria, precio, ${EMBEBIDO_IMPUESTOS}`)
    .eq('negocio_id', negocioId)
    .is('eliminado_en', null)
    .order('categoria')
    // El mismo criterio de `listarProductos`: el archivo sale en el orden en que se ve el catálogo.
    .order('sku');
  if (error) throw new ErrorDeApi(error, status);
  return (data ?? []).map((p) => [
    p.sku,
    p.nombre,
    p.categoria,
    Number(p.precio),
    sumarTarifas(impuestosDe(p.producto_impuestos)),
  ]);
}

export async function datosExporteTerceros(negocioId: string): Promise<(string | number | null)[][]> {
  const { data, error, status } = await supabase
    .from('terceros')
    .select('documento_tipo, documento_numero, documento_dv, nombre, telefono, correo, es_cliente, es_proveedor, municipio_codigo_dane')
    .eq('negocio_id', negocioId)
    .is('eliminado_en', null)
    .order('nombre');
  if (error) throw new ErrorDeApi(error, status);
  return (data ?? []).map((t) => [
    t.documento_tipo,
    t.documento_numero,
    t.documento_dv,
    t.nombre,
    t.telefono,
    t.correo,
    t.es_cliente ? 'Cliente' : t.es_proveedor ? 'Proveedor' : 'Otro',
    t.municipio_codigo_dane,
  ]);
}

type PagoExportado = { pago_monto: number; pago_orden: number; cuentas_negocio: { cuenta_nombre: string } | null };

/** El reparto en una celda: `null` cuando la venta tuvo un solo pago y basta el nombre de la cuenta. */
function desglosePagos(pagos: unknown): string | null {
  const filas = (pagos ?? []) as PagoExportado[];
  if (filas.length < 2) return null;
  return filas
    .slice()
    .sort((a, b) => a.pago_orden - b.pago_orden)
    .map((g) => `${(g.cuentas_negocio?.cuenta_nombre ?? 'sin cuenta').toLowerCase()} ${Math.round(Number(g.pago_monto))}`)
    .join('; ');
}

export async function datosExporteVentas(
  negocioId: string,
  anio: number,
  mes: number
): Promise<(string | number | null)[][]> {
  const desde = `${anio}-${String(mes).padStart(2, '0')}-01`;
  const hasta = mes === 12 ? `${anio + 1}-01-01` : `${anio}-${String(mes + 1).padStart(2, '0')}-01`;
  const { data, error, status } = await supabase
    .from('comprobantes')
    .select(
      'consecutivo, fecha, cuenta_nombre_congelado, medio_pago_dian, subtotal, impuesto, total, estado, comprobante_pagos ( pago_monto, pago_orden, cuentas_negocio:cuenta_id ( cuenta_nombre ) )'
    )
    .eq('negocio_id', negocioId)
    .gte('fecha', desde)
    .lt('fecha', hasta)
    .order('consecutivo');
  if (error) throw new ErrorDeApi(error, status);
  return (data ?? []).map((v) => [
    v.consecutivo,
    v.fecha,
    '222222222222', // adquiriente: consumidor final (los nominativos llegan en fases futuras)
    // Con un solo pago, el nombre de la cuenta como siempre; con varios, el reparto, porque el
    // contador conciliaría contra una sola cuenta una plata que entró por dos.
    desglosePagos(v.comprobante_pagos) ?? v.cuenta_nombre_congelado,
    v.medio_pago_dian,
    Number(v.subtotal),
    Number(v.impuesto),
    Number(v.total),
    v.estado,
  ]);
}
